import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, UserRound } from "lucide-react";
import api from "../lib/api";

const AGES = ["just now", "2 minutes ago", "8 minutes ago", "17 minutes ago", "34 minutes ago", "1 hour ago", "2 hours ago", "3 hours ago"];

const shuffle = (arr) => arr.map((v) => [Math.random(), v]).sort((a, b) => a[0] - b[0]).map(([, v]) => v);

const relTime = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} hour${h > 1 ? "s" : ""} ago` : "recently";
};

export const SocialProof = () => {
  const location = useLocation();
  const [current, setCurrent] = useState(null);
  const queue = useRef([]);
  const shown = useRef(0);
  const timers = useRef([]);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("sp_dismissed") === "1");

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    api.get("/social-proof").then(({ data }) => {
      if (cancelled || !data.enabled || !data.items.length) return;
      const real = data.items.filter((i) => i.real);
      const curated = shuffle(data.items.filter((i) => !i.real));
      queue.current = [...real, ...curated];
      const showNext = () => {
        if (shown.current >= 30 || !queue.current.length) return;
        const item = queue.current.shift();
        if (!item.real) queue.current.push(item);
        shown.current += 1;
        setCurrent({ ...item, age: item.at ? relTime(item.at) : AGES[Math.floor(Math.random() * AGES.length)] });
        timers.current.push(setTimeout(() => {
          setCurrent(null);
          timers.current.push(setTimeout(showNext, 8000 + Math.random() * 12000));
        }, 5000));
      };
      timers.current.push(setTimeout(showNext, 6000));
    }).catch(() => {});
    return () => { cancelled = true; timers.current.forEach(clearTimeout); };
  }, [dismissed]);

  const hidden = location.pathname.startsWith("/admin") || location.pathname.startsWith("/checkout") || location.pathname.startsWith("/payment");
  if (dismissed || hidden || !current) return null;

  return (
    <div data-testid="social-proof-widget"
      className="fixed bottom-4 left-4 z-50 max-w-[300px] bg-[#0B0F0D] border border-[#D4AF37]/40 rounded-2xl shadow-2xl p-3.5 flex items-start gap-3 animate-in slide-in-from-left duration-500">
      <span className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shrink-0">
        <UserRound size={17} className="text-emerald-100" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-white leading-snug" data-testid="social-proof-text">
          <span className="font-bold">{current.name}</span>
          {current.location ? <span className="text-gray-400"> from {current.location}</span> : null}
          {" "}<span className="text-[#D4AF37] font-semibold">{current.action}</span>
        </p>
        <p className="text-[11px] text-gray-500 mt-0.5">{current.age} · AI Shorthand</p>
      </div>
      <button data-testid="social-proof-close" aria-label="Dismiss notifications"
        onClick={() => { sessionStorage.setItem("sp_dismissed", "1"); setDismissed(true); }}
        className="text-gray-500 hover:text-white transition-colors shrink-0 p-0.5">
        <X size={14} />
      </button>
    </div>
  );
};
